import React, { useState, useRef } from 'react';
import axios from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';
import SEOHead from './SEOHead';
import companyConfig from '../config/companyConfig';
import { API_URL, API_ENDPOINTS } from '../config/apiConfig';

function Contact() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const recaptchaRef = useRef(null);
  const [recaptchaValue, setRecaptchaValue] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate reCAPTCHA (only if enabled)
    if (companyConfig.recaptcha.enabled !== false && !recaptchaValue) {
      setStatus({ type: 'error', message: 'Please complete the reCAPTCHA verification before submitting.' });
      return;
    }

    setLoading(true);
    setStatus({ type: '', message: '' });

    // Map frontend field names to backend expected names (moved outside try block)
    const backendData = {
      name: formData.fullName,
      email: formData.email,
      company: formData.company,
      phone: formData.phone,
      subject: formData.subject,
      message: formData.message,
      recaptchaToken: recaptchaValue
    };

    try {
      // Determine URL: If plain endpoint, prepend API_URL. If full URL (Formspree), use as is.
      const submitUrl = API_ENDPOINTS.contact.startsWith('http')
        ? API_ENDPOINTS.contact
        : `${API_URL}${API_ENDPOINTS.contact}`;

      await axios.post(submitUrl, backendData, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      setStatus({ type: 'success', message: 'Thank you for reaching out! We will get back to you within 24 hours.' });
      setFormData({ fullName: '', email: '', company: '', phone: '', subject: '', message: '' });
      setRecaptchaValue(null);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    } catch (error) {
      // Enhanced error logging
      console.error('Error:', error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('Server responded with:', JSON.stringify(error.response.data, null, 2));
        console.error('Status code:', error.response.status);
        console.error('Headers:', error.response.headers);
        console.error('Request data sent:', JSON.stringify(backendData, null, 2));

        // Show specific error message from server if available
        const errorMessage = error.response.data?.details
          ? error.response.data.details.join(', ')
          : error.response.data?.error || 'Something went wrong. Please try again later.';
        setStatus({ type: 'error', message: errorMessage });
      } else if (error.request) {
        // The request was made but no response was received
        console.error('No response received:', error.request);
        setStatus({ type: 'error', message: 'No response from server. Please check your connection.' });
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Error setting up request:', error.message);
        setStatus({ type: 'error', message: 'Error setting up request. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRecaptchaChange = (value) => {
    setRecaptchaValue(value);
  };

  return (
    <>
      <SEOHead
        title="Contact Time2Find - Get Expert IT Solutions & Consultation"
        description="Contact Time2Find for expert IT consulting, SAP implementation, cloud solutions, cybersecurity, and digital transformation. Get a free consultation from our experienced team."
        keywords="contact Time2Find, IT consultation, SAP consulting, enterprise solutions, free quote, business inquiry, technology consulting, expert advice"
        canonical="https://time2find.com/contact"
        schemaType="ContactPage"
      />

      <div className="contact-page-new">
        {/* Hero Section with Background */}
        <section className="contact-hero-new">
          <div className="contact-hero-overlay"></div>
          <div className="contact-hero-content-new">
            <div className="contact-hero-badge">
              <i className="fas fa-envelope"></i>
            </div>
            <h1 className="contact-hero-title">Get In Touch</h1>
            <p className="contact-hero-subtitle">
              Have a question or ready to start your digital transformation? We're here to help.
            </p>
          </div>
        </section>

        {/* Main Contact Section */}
        <section className="contact-main-section">
          <div className="contact-container-new">

            {/* Contact Form - Centered Professional Layout */}
            <div className="contact-form-wrapper-new">
              <div className="contact-form-header-new">
                <div className="form-icon-badge">
                  <i className="fas fa-comments"></i>
                </div>
                <h2 className="form-main-title">Send Us a Message</h2>
                <p className="form-main-subtitle">
                  Fill out the form below and our team will get back to you within 24 hours
                </p>
              </div>

              {status.message && (
                <div className={`form-alert ${status.type}`}>
                  <i className={`fas ${status.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'}`}></i>
                  <span>{status.message}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="contact-form-new">
                <div className="form-row">
                  <div className="form-field-new">
                    <label htmlFor="fullName" className="form-label-new">
                      <i className="fas fa-user"></i> Full Name *
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder="John Doe"
                      className="form-input-new"
                      autoComplete="name"
                      required
                      disabled={loading}
                    />
                  </div>

                  <div className="form-field-new">
                    <label htmlFor="email" className="form-label-new">
                      <i className="fas fa-envelope"></i> Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@company.com"
                      className="form-input-new"
                      autoComplete="email"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field-new">
                    <label htmlFor="company" className="form-label-new">
                      <i className="fas fa-building"></i> Company Name
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Your Company"
                      className="form-input-new"
                      autoComplete="organization"
                      disabled={loading}
                    />
                  </div>

                  <div className="form-field-new">
                    <label htmlFor="phone" className="form-label-new">
                      <i className="fas fa-phone"></i> Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+1 (555) 000-0000"
                      className="form-input-new"
                      autoComplete="tel"
                      required
                      disabled={loading}
                    />
                    <small style={{ color: '#666', fontSize: '0.85rem', marginTop: '5px', display: 'block' }}>
                      <i className="fas fa-info-circle"></i> Include country code (e.g., +1, +91)
                    </small>
                  </div>
                </div>

                <div className="form-field-new full-width">
                  <label htmlFor="subject" className="form-label-new">
                    <i className="fas fa-tag"></i> Subject *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="How can we help you?"
                    className="form-input-new"
                    autoComplete="off"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-field-new full-width">
                  <label htmlFor="message" className="form-label-new">
                    <i className="fas fa-comment-dots"></i> Your Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us more about your project or inquiry..."
                    className="form-textarea-new"
                    rows="6"
                    autoComplete="off"
                    required
                    disabled={loading}
                  ></textarea>
                  <small style={{ color: '#666', fontSize: '0.85rem', marginTop: '5px', display: 'block' }}>
                    <i className="fas fa-info-circle"></i> Please provide at least 15 words for better clarity
                  </small>
                </div>

                {/* reCAPTCHA */}
                {companyConfig.recaptcha.enabled !== false && (
                  <div className="form-field-new full-width recaptcha-container">
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={companyConfig.recaptcha.siteKey}
                      onChange={handleRecaptchaChange}
                      theme="light"
                    />
                    <p className="recaptcha-privacy">
                      <i className="fas fa-shield-alt"></i>
                      Protected by reCAPTCHA. <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy</a> and <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms</a>
                    </p>
                  </div>
                )}

                <button type="submit" className="form-submit-new" disabled={loading}>
                  {loading ? (
                    <>
                      <i className="fas fa-robot fa-bounce" style={{ marginRight: '8px' }}></i>
                      <i className="fas fa-cog fa-spin" style={{ marginRight: '8px' }}></i>
                      Processing your message...
                      <i className="fas fa-rocket" style={{ marginLeft: '8px', animation: 'slideRight 1s infinite' }}></i>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane"></i> Send Message
                    </>
                  )}
                </button>
              </form>

              {/* Info Cards Below Form */}
              <div className="contact-info-cards">
                <div className="info-card-new">
                  <div className="info-card-icon blue">
                    <i className="fas fa-clock"></i>
                  </div>
                  <h4 className="info-card-title">Response Time</h4>
                  <p className="info-card-text">Within 24 hours</p>
                </div>

                <div className="info-card-new">
                  <div className="info-card-icon orange">
                    <i className="fas fa-headset"></i>
                  </div>
                  <h4 className="info-card-title">24/7 Support</h4>
                  <p className="info-card-text">Always Available</p>
                </div>

                <div className="info-card-new">
                  <div className="info-card-icon blue">
                    <i className="fas fa-globe"></i>
                  </div>
                  <h4 className="info-card-title">Global Reach</h4>
                  <p className="info-card-text">Worldwide Service</p>
                </div>

                <div className="info-card-new">
                  <div className="info-card-icon orange">
                    <i className="fas fa-shield-alt"></i>
                  </div>
                  <h4 className="info-card-title">Secure</h4>
                  <p className="info-card-text">Data Protected</p>
                </div>

                <div className="info-card-new">
                  <div className="info-card-icon blue">
                    <i className="fas fa-users"></i>
                  </div>
                  <h4 className="info-card-title">Expert Team</h4>
                  <p className="info-card-text">25+ Years Experience</p>
                </div>

                <div className="info-card-new">
                  <div className="info-card-icon orange">
                    <i className="fas fa-rocket"></i>
                  </div>
                  <h4 className="info-card-title">Fast Setup</h4>
                  <p className="info-card-text">Quick Onboarding</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Information Section */}
        <section className="office-locations-section">
          <div className="office-locations-container">
            <div className="office-locations-header">
              <h2 className="office-locations-title">Contact Information</h2>
              <p className="office-locations-subtitle">
                Get in touch with us through any of these channels
              </p>
            </div>

            <div className="office-locations-grid">
              {/* Main Office */}
              <div className="office-card">
                <div className="office-icon blue-gradient">
                  <i className="fas fa-building"></i>
                </div>
                <h3 className="office-city">Our Office</h3>
                <div className="office-details">
                  <div className="office-detail-item">
                    <i className="fas fa-map-marker-alt"></i>
                    <p>{companyConfig.contact.fullAddress}</p>
                  </div>
                  <div className="office-detail-item">
                    <i className="fas fa-phone"></i>
                    <a href={`tel:${companyConfig.contact.phoneRaw}`} className="contact-link">
                      {companyConfig.contact.phone}
                    </a>
                  </div>
                  <div className="office-detail-item">
                    <i className="fas fa-envelope"></i>
                    <a href={`mailto:${companyConfig.contact.email}`} className="contact-link">
                      {companyConfig.contact.email}
                    </a>
                  </div>
                  <div className="office-detail-item">
                    <i className="fas fa-clock"></i>
                    <p>Mon-Fri: 9:00 AM - 6:00 PM</p>
                  </div>
                </div>
              </div>

              {/* WhatsApp Contact */}
              <div className="office-card">
                <div className="office-icon orange-gradient">
                  <i className="fab fa-whatsapp"></i>
                </div>
                <h3 className="office-city">WhatsApp</h3>
                <div className="office-details">
                  <div className="office-detail-item">
                    <i className="fab fa-whatsapp"></i>
                    <p>Chat with us on WhatsApp</p>
                  </div>
                  <div className="office-detail-item">
                    <i className="fas fa-mobile-alt"></i>
                    <a
                      href={`https://wa.me/${companyConfig.contact.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(companyConfig.contact.whatsappMessage)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="contact-link whatsapp-link"
                    >
                      {companyConfig.contact.whatsapp}
                    </a>
                  </div>
                  <div className="office-detail-item">
                    <i className="fas fa-comments"></i>
                    <p>Instant messaging support</p>
                  </div>
                  <div className="office-detail-item">
                    <i className="fas fa-clock"></i>
                    <p>24/7 Available</p>
                  </div>
                </div>
              </div>

              {/* Quick Links */}
              <div className="office-card">
                <div className="office-icon blue-gradient">
                  <i className="fas fa-link"></i>
                </div>
                <h3 className="office-city">Quick Links</h3>
                <div className="office-details">
                  <div className="office-detail-item">
                    <i className="fas fa-briefcase"></i>
                    <a href="/services" className="contact-link">View Our Services</a>
                  </div>
                  <div className="office-detail-item">
                    <i className="fas fa-info-circle"></i>
                    <a href="/about" className="contact-link">About {companyConfig.company.name}</a>
                  </div>
                  <div className="office-detail-item">
                    <i className="fas fa-user-tie"></i>
                    <a href="/career" className="contact-link">Join Our Team</a>
                  </div>
                  <div className="office-detail-item">
                    <i className="fas fa-shield-alt"></i>
                    <a href="/privacy-policy" className="contact-link">Privacy Policy</a>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div className="office-card">
                <div className="office-icon orange-gradient">
                  <i className="fas fa-share-alt"></i>
                </div>
                <h3 className="office-city">Follow Us</h3>
                <div className="office-details">
                  <div className="office-detail-item">
                    <i className="fab fa-linkedin"></i>
                    <a href={companyConfig.social.linkedin} target="_blank" rel="noopener noreferrer" className="contact-link">
                      LinkedIn
                    </a>
                  </div>
                  <div className="office-detail-item">
                    <i className="fab fa-facebook"></i>
                    <a href={companyConfig.social.facebook} target="_blank" rel="noopener noreferrer" className="contact-link">
                      Facebook
                    </a>
                  </div>
                  <div className="office-detail-item">
                    <i className="fab fa-instagram"></i>
                    <a href={companyConfig.social.instagram} target="_blank" rel="noopener noreferrer" className="contact-link">
                      Instagram
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Loading Animation Styles */}
        <style jsx>{`
        @keyframes slideRight {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(10px); }
        }
        
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        
        .fa-bounce {
          animation: bounce 0.6s infinite;
        }
      `}</style>
      </div>
    </>
  );
}

export default Contact;